import type { EnvType } from 'types';
import type { IPurpose } from 'views/pipelines/components';

import { FC, useCallback, useEffect, useMemo, useRef, useState, CSSProperties } from 'react';
import { Loader } from '@storybook';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';
import { loadEsignWebComponents } from 'utils/load-esign-web-components';
import { loginState } from 'global-stores';
import { ROUTES } from 'routes';

import './simplici-sign.scss';

interface ISimpliciSign {
	env: EnvType;
	signUrl: string;
	handleSubmitModal: (purpose?: IPurpose) => void;
	/**
	 * Short-lived `evault`-scoped token. Only the `Vault this agreement` panel needs it
	 * (listing vaults is authenticated); every signing flow works without it.
	 */
	vaultToken?: string;
}

export const SimpliciSignWebComponent: FC<ISimpliciSign> = ({
	env,
	signUrl,
	handleSubmitModal,
	vaultToken,
}) => {
	const [loading, setLoading] = useState(false);
	const mountRef = useRef<HTMLDivElement>(null);
	const elementRef = useRef<HTMLElement | null>(null);
	const navigate = useNavigate();

	// Pass the authenticated sender's JWT into the widget so esign.api can
	// authenticate sender operations (ESIGN-C-01). Empty for unauthenticated
	// contexts; the widget then behaves as an anonymous signer.
	const { accessToken } = useRecoilValue(loginState) || {};

	/* The embedded eSign widget cannot reach this app's router; it raises a window
	   event when the applicant tries to vault an agreement without the add-on. */
	useEffect(() => {
		const onAddonRequired = () => navigate(`${ROUTES.BILLING}#billing-evault-addon`);
		window.addEventListener('simplici:evault-addon-required', onAddonRequired);
		return () => window.removeEventListener('simplici:evault-addon-required', onAddonRequired);
	}, [navigate]);

	useEffect(() => {
		let cancelled = false;

		// Shared with the eVault host: the esign bundle registers both
		// <simplici-sign> and <simplici-evault>, and injecting it twice makes the
		// second customElements.define() throw.
		loadEsignWebComponents().then(() => {
			if (!cancelled) {
				setLoading(false);
			}
		});

		return () => {
			cancelled = true;
		};
	}, []);

	/* The widget calls this when signing finishes. Kept in a ref so the function
	   handed to the element (once, below) always reaches the latest callback. */
	const handleSubmitRef = useRef(handleSubmitModal);
	handleSubmitRef.current = handleSubmitModal;
	const submitEsign = useCallback((status: boolean, purpose: IPurpose) => {
		if (status) {
			handleSubmitRef.current(purpose);
		}
	}, []);

	/**
	 * The element is created imperatively rather than through
	 * `dangerouslySetInnerHTML`, which is how this component used to build it.
	 *
	 * Two reasons. `vaultToken` and the sender `token` are assigned as DOM
	 * **properties**, so a bearer token never appears in serialised markup that
	 * anything on the page can read back. And interpolating `env`/`signUrl` into an
	 * HTML string breaks the moment one of them contains a quote — the old markup was
	 * also unquoted (`env=${env}`), which is only safe by accident.
	 */
	useEffect(() => {
		if (loading || !mountRef.current) return;

		let cancelled = false;
		customElements.whenDefined('simplici-sign').then(() => {
			if (cancelled || !mountRef.current) return;

			if (!elementRef.current) {
				elementRef.current = document.createElement('simplici-sign');
				mountRef.current.appendChild(elementRef.current);
			}

			const element = elementRef.current as HTMLElement & {
				env?: string;
				url?: string;
				submit_esign?: (status: boolean, purpose: IPurpose) => void;
				token?: string;
				vault_token?: string;
			};
			element.env = env;
			element.setAttribute('data-host', 'admin.panel');
			element.url = signUrl;
			// THE FUNCTION ITSELF, not a global's name. react-to-webcomponent looks a
			// `Function` prop up on `window` only when it arrives as an ATTRIBUTE; a
			// property is passed through untouched. When this element moved from
			// markup to properties the value stayed 'handleSubmitModal', so the
			// widget got a string and threw "handleFinish is not a function" right
			// after the signature was saved: the modal sat on "Document signing
			// completed" and the inbox never refetched, so the packet kept reading
			// NEED TO SIGN.
			element.submit_esign = submitEsign;
			// ESIGN-C-01: empty string for unauthenticated contexts, where the widget
			// falls back to anonymous-signer behaviour.
			element.token = accessToken ?? '';
			if (vaultToken) {
				element.vault_token = vaultToken;
			}
		});

		return () => {
			cancelled = true;
		};
	}, [loading, env, signUrl, accessToken, vaultToken, submitEsign]);

	if (loading) {
		return (
			<div className="simplici-sign-loader">
				<Loader />
			</div>
		);
	}

	// `env`, `url`, `token` and `vault_token` are set as properties in the effect
	// above, so no markup is serialised here.
	return <div ref={mountRef} className="simplici-sign-host" />;
};

interface ISimpliciSIgnIframe {
	signUrl: string;
	handleSubmitModal: () => void;
	styles?: CSSProperties;
	title?: string;
	className?: string;
	onLoad?: () => void;
	id?: string;
}

// SAT-1056: the embedded eSign app authenticates its sender-side requests with
// the platform JWT. Mounted as a web component it reads our `user` cookie
// directly (it shares this document); embedded here as a CROSS-ORIGIN iframe it
// cannot — document.cookie inside the frame belongs to the esign origin. So its
// requests went out with no credential, which esign.api answers with a bare 404
// once AUTHZ_ENFORCED is on (enforce mode throws NotFound so as not to leak
// whether the resource exists), and the editor never leaves its loader.
//
// The frame asks us for the token and we hand it over. Not via the URL: a
// platform JWT in a query string lands in browser history, the Referer header
// and the ingress access log.
const SENDER_TOKEN_REQUEST = 'esign_sender_token_request';
const SENDER_TOKEN_RESPONSE = 'esign_sender_token';

// The send modal's `Vault this agreement` panel needs an `evault`-scoped token
// on top of the sender JWT — esign.api's eVault routes accept nothing else. The
// web-component hosts pass one as `vault_token`; a frame cannot receive props,
// so without this the panel waited for a token that never came and sat on
// "Checking your plan…" with Continue blocked. Minted on REQUEST rather than on
// mount, so a frame that only signs never costs a `POST /vault-tokens`.
const VAULT_TOKEN_REQUEST = 'esign_vault_token_request';
const VAULT_TOKEN_RESPONSE = 'esign_vault_token';

const readSenderToken = (): string => {
	try {
		const prefix = 'user=';
		const raw = decodeURIComponent(document.cookie)
			.split(';')
			.map(entry => entry.trim())
			.find(entry => entry.indexOf(prefix) === 0);
		if (!raw) return '';
		return JSON.parse(raw.substring(prefix.length))?.accessToken ?? '';
	} catch {
		return '';
	}
};

export const SimpliciSignIframe: FC<ISimpliciSIgnIframe> = ({
	signUrl,
	handleSubmitModal,
	styles = {},
	onLoad = () => ({}),
	title = 'untitled-iframe',
	className = '',
	id,
}) => {
	const frameRef = useRef<HTMLIFrameElement>(null);
	const { post } = useNetwork({ updateState: false });
	const postRef = useRef(post);
	postRef.current = post;

	useEffect(() => {

		 const messageHandler = async (event: any) => {
			const receivedData: {
				type:
					| 'submit_esign'
					| typeof SENDER_TOKEN_REQUEST
					| typeof VAULT_TOKEN_REQUEST;
				data: Record<string, never>;
			} = event.data;

			if (receivedData?.type === 'submit_esign') {
				handleSubmitModal();
				return;
			}

			if (
				receivedData?.type !== SENDER_TOKEN_REQUEST &&
				receivedData?.type !== VAULT_TOKEN_REQUEST
			) {
				return;
			}

			// Answer only the frame we rendered, and only to the origin we
			// pointed it at — never to `*`, which would hand the token to any
			// listener.
			if (
				!frameRef.current ||
				event.source !== frameRef.current.contentWindow
			) {
				return;
			}
			let frameOrigin = '';
			try {
				frameOrigin = new URL(signUrl, window.location.href).origin;
			} catch {
				return;
			}

			if (receivedData.type === SENDER_TOKEN_REQUEST) {
				event.source.postMessage(
					{ type: SENDER_TOKEN_RESPONSE, token: readSenderToken() },
					frameOrigin
				);
				return;
			}

			// An empty token is still an answer: the frame shows "could not check
			// your plan" with a retry instead of waiting out its timeout.
			let token = '';
			try {
				const result = await postRef.current(API_URL.VAULT_TOKENS, {});
				token = result?.data?.token ?? result?.token ?? '';
			} catch {
				token = '';
			}
			event.source.postMessage(
				{ type: VAULT_TOKEN_RESPONSE, token },
				frameOrigin
			);
		};

		window.addEventListener('message', messageHandler);
		 // Removing event listener on unmounting the modal
		return () => {
            window.removeEventListener('message', messageHandler);
        };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signUrl]);

	/**
	 * The theme has to travel IN THE URL, because an iframe inherits nothing.
	 *
	 * Everywhere esign.frontend is mounted as a light-DOM web component it
	 * follows the panel for free: the sv2 design system repoints the
	 * `--color-*-light` family in the scope the widget sits in, and CSS
	 * inheritance does the rest. A frame is a separate document with its own
	 * `:root`, so none of that reaches it and the Templates flow's upload modal
	 * and editor rendered white on a dark panel.
	 *
	 * A param rather than a postMessage: a message can only land after load, so
	 * the frame would paint light and then flip on every open. The URL is known
	 * before the first rule is applied.
	 *
	 * DELIBERATELY NOT REACTIVE TO THE THEME. `src` is read on each render, and
	 * changing it RELOADS the frame — which, mid-edit, would throw away the
	 * document the user is configuring. The theme is therefore sampled only when
	 * the URL itself changes; the toggle that could flip it sits behind a
	 * full-screen modal while this is open, so there is nothing to miss.
	 */
	const themedUrl = useMemo(() => {
		if (!signUrl) return signUrl;
		try {
			const url = new URL(signUrl, window.location.href);
			const isDark =
				document.documentElement.getAttribute('data-sv2-theme') === 'dark';
			url.searchParams.set('theme', isDark ? 'dark' : 'light');
			return url.toString();
		} catch {
			// A relative or malformed URL is still worth rendering, just unthemed.
			return signUrl;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [signUrl]);

	return (
		<iframe
			ref={frameRef}
			src={themedUrl}
			style={{ ...styles }}
			height="90vh"
			width="85vw"
			title={title}
			id={id}
			className={className}
			onLoad={onLoad}
			allow="geolocation"
		></iframe>
	);
};
